import {
  isUnmappedPageId,
  matchesProposalCoverageType,
} from "@platform/mpx-quotes-grid";

import type { Proposal } from "@platform/mpx-quotes-grid";
import type { CoverageTypeEnum } from "@platform/types";

/**
 * A carrier eligible for the Quote History view: it has at least one archived quote — inactive
 * and actioned — for the SBS page on screen. An un-actioned pending requote does not qualify.
 */
export interface HistoryCarrierOption {
  /** Carrier id (BTX proposal carrierId / carrier shareIdentifier). */
  id: string;
  /** Display name (from the name-resolved proposals passed in). */
  name: string;
  /**
   * How many archived quotes this carrier has for the SBS page on screen — the extra columns
   * that appear if it is selected. Only carriers with `historyCount >= 1` are offered.
   */
  historyCount: number;
}

type ProposalPlanGroup = Proposal["coverages"][number]["planGroups"][number];

/**
 * The plan groups this proposal contributes to the SBS page identified by `selectedBpgId` —
 * i.e. those quoting that page's RFP-requested plan. On the synthetic "__unmapped__" page
 * there is no requested plan to match, so unmapped groups count instead, and the coverage has
 * to be checked because a null FK is not unique across LOCs.
 */
const groupsOnThisPage = (
  proposal: Proposal,
  coverageType: CoverageTypeEnum | undefined,
  selectedBpgId: string | undefined,
): ProposalPlanGroup[] =>
  proposal.coverages.flatMap((coverage) => {
    // Synthesized "awaiting quote" placeholders carry the requested-plan FK too, so matching
    // that FK alone would count scaffolding as a real quote and offer the carrier on pages it
    // never quoted (CD-1793). Only real quoted groups count.
    const quotedGroups = (coverage.planGroups ?? []).filter(
      (group) => !group.isPlaceholder,
    );

    const inCoverage =
      coverageType == null ||
      matchesProposalCoverageType(coverage.coverageType, coverageType);

    if (isUnmappedPageId(selectedBpgId)) {
      return inCoverage
        ? quotedGroups.filter((group) => group.rfpBenefitPlanGroupId == null)
        : [];
    }

    // The page's requested plan isn't resolved yet (first render, or a URL without `bpg`), so
    // there is nothing to scope to and the LOC is the best available filter — but a real quoted
    // group is still required, or scaffolding and empty coverages would qualify. Returned
    // before the id comparison below on purpose: an unmapped group's id is undefined too, so
    // comparing them would match every unmapped group on every page.
    if (selectedBpgId == null) return inCoverage ? quotedGroups : [];

    // A requested-plan id is unique across the RFP, so matching it is inherently
    // coverage-correct — no need to also match the LOC.
    return quotedGroups.filter(
      (group) => group.rfpBenefitPlanGroupId === selectedBpgId,
    );
  });

/**
 * True for a quote that Quote History would reveal: superseded as the active version, or
 * disregarded. Both land inactive and actioned.
 *
 * State is read from the plan group, falling back per field to the owning proposal for a
 * group that has never had that column written. The two fields resolve independently: a
 * Disregard writes the review state and may leave the active flag unset, and Replace /
 * Disregard / Restore never write back to the proposal row at all, so its fields go stale as
 * soon as one runs — reading them alone reported no history for a carrier that had some.
 *
 * An un-actioned pending requote is excluded on purpose. It is already on the board under the
 * `board` read scope, so selecting its carrier adds nothing — and the count is defined as how
 * many extra plans appear when you turn history on. A carrier whose only non-active quote is
 * pending therefore counts zero and is not offered at all.
 */
const isArchivedGroup = (
  group: ProposalPlanGroup,
  proposal: Proposal,
): boolean => {
  // Deletion is only permitted on the active version, so a deleted group still reports
  // isActiveVersion=true and would otherwise count as the live quote rather than history.
  if (group.deletedAt) return true;

  const isActiveVersion = group.isActiveVersion ?? proposal.isActiveVersion;
  const requoteReviewState =
    group.requoteReviewState ?? proposal.requoteReviewState;

  return !isActiveVersion && requoteReviewState !== "pending";
};

/**
 * True when the proposal backs an active column *on this page* — read at plan group grain,
 * since a plan-group-grain Replace leaves the promoted requote's proposal row reading
 * inactive.
 *
 * Scoping to the page is what keeps one carrier's history off another's board. A single
 * AI-parsed proposal spans many coverages and can be the active version on one while sitting
 * as a pending requote on another; asking whether it is active *anywhere* kept it for the
 * whole board, so its unrelated pending column surfaced on a page whose carrier the analyst
 * never selected.
 *
 * A proposal contributing no groups to this page has nothing finer-grained to consult and
 * falls back to its own state, mirroring prunePlanGroupsByScope's whole-proposal fallback.
 */
const hasActiveGroupOnThisPage = (
  proposal: Proposal,
  coverageType: CoverageTypeEnum | undefined,
  selectedBpgId: string | undefined,
): boolean => {
  const pageGroups = groupsOnThisPage(proposal, coverageType, selectedBpgId);

  if (pageGroups.length === 0) return proposal.isActiveVersion;

  return pageGroups.some(
    (group) =>
      !group.deletedAt && (group.isActiveVersion ?? proposal.isActiveVersion),
  );
};

/**
 * Builds the Quote History carrier list (ADR-031) from the all-scope proposals, scoped to the
 * SBS page on screen — its line of coverage *and* its RFP-requested plan.
 *
 * `historyCount` is how many archived quotes the carrier has for that page, which is how many
 * extra columns appear if the analyst selects it. Carriers with none are not offered.
 *
 * Two things this deliberately does not do:
 *
 *   - It does not derive eligibility from counting distinct `rfpVersion` values. A carrier's
 *     repeat quotes routinely share one RFP version — every document-origin proposal lands at
 *     the RFP's current version, which is why proposals are disambiguated by
 *     `proposal_sequence` — so version distinctness both missed carriers that had archived
 *     quotes and offered carriers whose versions were all active with nothing to show.
 *   - It does not ask merely whether the proposal touches this LOC. One AI-parsed proposal can
 *     span eight coverages, which offered its carrier on every page it brushed against
 *     (CD-1793). Contributing a column to *this* page is the test.
 *
 * Deleted plan groups are not counted: the proposals payload omits soft-deleted groups
 * entirely, so it cannot be done on the client. Tracked separately.
 */
export const buildHistoryCarrierOptions = (
  proposals: Proposal[],
  coverageType: CoverageTypeEnum | undefined,
  selectedBpgId: string | undefined,
): HistoryCarrierOption[] => {
  const historyCountByCarrier = new Map<string, number>();
  const nameByCarrier = new Map<string, string>();

  for (const proposal of proposals) {
    const pageGroups = groupsOnThisPage(proposal, coverageType, selectedBpgId);
    if (pageGroups.length === 0) continue;

    // Callers pass name-resolved proposals, so carrierName is the display name.
    nameByCarrier.set(proposal.carrierId, proposal.carrierName);

    const archivedOnThisPage = pageGroups.filter((group) =>
      isArchivedGroup(group, proposal),
    ).length;

    const current = historyCountByCarrier.get(proposal.carrierId) ?? 0;
    historyCountByCarrier.set(proposal.carrierId, current + archivedOnThisPage);
  }

  const options: HistoryCarrierOption[] = [];
  for (const [carrierId, historyCount] of historyCountByCarrier) {
    if (historyCount < 1) continue;
    options.push({
      id: carrierId,
      name: nameByCarrier.get(carrierId) ?? carrierId,
      historyCount,
    });
  }

  return options.sort((a, b) => a.name.localeCompare(b.name));
};

/**
 * Narrows the SBS proposals for the Quote History view (CD-1789).
 *
 * Turning history on for a carrier reveals that carrier's past quotes; it must not take the
 * rest of the board away with it. So every carrier keeps its active quote, and only the
 * non-active versions — archived predecessors and un-actioned pending requotes — are
 * restricted to the selected carriers. A non-selected carrier's pending requote is therefore
 * hidden while history is on, which is intended: the view is about one carrier's history
 * alongside everyone's current standing.
 *
 * "Active" is judged for the SBS page on screen, not the proposal as a whole — see
 * hasActiveGroupOnThisPage.
 *
 * The synthetic Incumbent/Requested column is built from RFP data rather than a proposal
 * (see useQuotesPresentationGridData), so it is unaffected and always shows.
 *
 * With no carrier selected the list is returned as-is, by identity, so callers memoizing on
 * it don't rebuild the grid.
 */
export const filterProposalsForHistoryView = (
  proposals: Proposal[],
  selectedCarrierIds: string[],
  coverageType: CoverageTypeEnum | undefined,
  selectedBpgId: string | undefined,
): Proposal[] => {
  if (selectedCarrierIds.length === 0) return proposals;

  const selected = new Set(selectedCarrierIds);

  return proposals.filter(
    (proposal) =>
      hasActiveGroupOnThisPage(proposal, coverageType, selectedBpgId) ||
      selected.has(proposal.carrierId),
  );
};
